import Logo from '@/components/Logo.js';
import { SnackbarProvider } from '@/context/SnackbarContext.js';
import { useFirebaseAuth } from '@/utils/firebase.js';
import { Box, Stack } from '@mui/material';
import { Card } from '@repo/ui';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import EmailVerification from '../pages/auth/signup/EmailVerification.js';
import { useCurrentZubeAgentUser } from '../utils/api.js';
import MagicLoading from './MagicLoading.js';

/**
 * Layout for agent authentication pages (login, signup)
 * Handles routing based on user authentication state
 */
const AgentAuthLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: firebaseUser, isInitialized } = useFirebaseAuth();
  const [showEmailVerification, setShowEmailVerification] = useState(false);

  // Use the zubeAgents getCurrentUser endpoint
  const {
    data: agentUser,
    isLoading,
    isError,
    isFetched,
    fetchStatus,
    refetch,
  } = useCurrentZubeAgentUser({
    enabled: isInitialized && !!firebaseUser,
    showErrorSnackbar: false
  });

  // Base auth paths that are allowed without authentication
  const baseAuthPaths = useMemo(() => ['/agent/auth/login', '/agent/auth/signup'], []);

  useEffect(() => {
    if (!isInitialized) return;

    const currentPath = location.pathname;

    // If not authenticated, allow login and signup pages
    if (!firebaseUser) {
      setShowEmailVerification(false);
      if (!baseAuthPaths.includes(currentPath)) {
        navigate('/agent/auth/login');
      }
      return;
    }

    // Block access until the email is verified
    if (!firebaseUser.emailVerified) {
      setShowEmailVerification(true);
      return;
    } else if (showEmailVerification) {
      setShowEmailVerification(false);
    }

    // If authenticated and we have the agent user, redirect to agent dashboard
    if (agentUser?.data) {
      navigate('/agent');
      return;
    }

    // If there's an error and we're not on an auth page, redirect to login
    if (isError && !baseAuthPaths.includes(currentPath)) {
      navigate('/agent/auth/login');
    }
  }, [firebaseUser, agentUser, isError, isInitialized, location.pathname, navigate, isLoading, baseAuthPaths, showEmailVerification]);

  useEffect(() => {
    if (!isInitialized || !firebaseUser || showEmailVerification) return;
    if (agentUser?.data || isError || isLoading) return;
    if (isFetched || fetchStatus !== 'idle') return;

    void refetch();
  }, [agentUser?.data, fetchStatus, firebaseUser, isError, isFetched, isInitialized, isLoading, refetch, showEmailVerification]);

  // Show loading while checking auth or waiting for user data after login
  const isWaitingForUserData = firebaseUser && !agentUser?.data && !isError;
  if ((!isInitialized || isLoading || isWaitingForUserData) && !showEmailVerification) {
    return (
      <MagicLoading
        active
        onMagic={() => navigate('/agent/auth/login')}
      />
    );
  }

  return (
    <SnackbarProvider>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          py: { xs: 2, md: 4, lg: 8 }
        }}
        spacing={2}
      >
        <Stack flex={1} direction='row' justifyContent='center'>
          <Logo height={72} />
        </Stack>
        <Box sx={{ maxWidth: 'sm', flex: 1, width: '100%' }}>
          <Card
            sx={{
              p: 4,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Outlet />
          </Card>
        </Box>
        <Box flex={1} />

        {/* Email verification dialog */}
        {firebaseUser && (
          <EmailVerification
            email={firebaseUser.email || ''}
            open={showEmailVerification}
          />
        )}
      </Stack>
    </SnackbarProvider>
  );
};

export default AgentAuthLayout;
